import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { getPaymentReportPdfByDate, getPaymentStats } from "../services/reports";
import { MONTHS, formatCurrencyMZN } from "../utils/paymentHelpers";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const currentMonth = String(new Date().getMonth() + 1);

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// Mirrors sge_infantil_core's utils/DatetimeUtils.js#getDateRange so the
// preview shown in Data Início/Data Fim matches exactly what the backend
// resolves for the same Dia or Mês/Ano selection.
const resolveDateRange = ({ day, year, month }) => {
  const now = new Date();

  if (day === "hoje") {
    const iso = toISODate(now);
    return { startDate: iso, endDate: iso };
  }
  if (day === "ontem") {
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const iso = toISODate(yesterday);
    return { startDate: iso, endDate: iso };
  }
  if (day === "semana") {
    const diffToMonday = (now.getDay() + 6) % 7;
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - diffToMonday);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return { startDate: toISODate(startOfWeek), endDate: toISODate(endOfWeek) };
  }

  const firstDay = new Date(year, Number(month) - 1, 1);
  const lastDay = new Date(year, Number(month), 0);
  return { startDate: toISODate(firstDay), endDate: toISODate(lastDay) };
};

const RelatorioPagamentosLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [type, setType] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Day filter for PDF generation — mirrors sge_infantil_web's report/student
  // screen (the "Relatório de Pagamentos" report there). Data Início/Data Fim
  // are pre-filled from resolveDateRange (kept in lockstep with
  // sge_infantil_core's utils/DatetimeUtils.js#getDateRange) whenever Ano,
  // Mês or Dia change, but stay editable so the user can still override them
  // with a custom range — editing either one clears Dia, since a manually
  // picked range takes priority over a Dia preset.
  const [day, setDay] = useState("");
  const [startDate, setStartDate] = useState(
    () => resolveDateRange({ day: "", year: currentYear, month: currentMonth }).startDate
  );
  const [endDate, setEndDate] = useState(
    () => resolveDateRange({ day: "", year: currentYear, month: currentMonth }).endDate
  );

  const applyDateRange = (nextDay, nextYear, nextMonth) => {
    const range = resolveDateRange({ day: nextDay, year: nextYear, month: nextMonth });
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const handleYearChange = (value) => {
    setYear(value);
    applyDateRange(day, value, month);
  };

  const handleMonthChange = (value) => {
    setMonth(value);
    applyDateRange(day, year, value);
  };

  const handleDayChange = (value) => {
    setDay(value);
    applyDateRange(value, year, month);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setDay("");
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setDay("");
  };

  const fetchStats = () => {
    setLoading(true);
    getPaymentStats({ year, month, type })
      .then((resp) => setStats((resp.data || [])[0] || null))
      .catch(() => toast.error(t("relatorioPagamentos.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, type, endDate]);

  const handleGenerate = async () => {
    if (!day && (startDate || endDate) && !(startDate && endDate)) {
      toast.error(t("relatorioPagamentos.error.dateRange"));
      return;
    }

    setGenerating(true);
    try {
      let resp;
      let title;
      let filename;

      if (day) {
        resp = await getPaymentReportPdfByDate({ day, type });
        title = t("relatorioPagamentos.documento.titleDay", { day: t(`relatorioPagamentos.day.${day === "hoje" ? "today" : day === "ontem" ? "yesterday" : "week"}`) });
        filename = `relatorio-pagamentos-${day}.pdf`;
      } else if (startDate && endDate) {
        resp = await getPaymentReportPdfByDate({ startDate, endDate, type });
        title = t("relatorioPagamentos.documento.titleRange", { startDate, endDate });
        filename = `relatorio-pagamentos-${startDate}-a-${endDate}.pdf`;
      } else {
        resp = await getPaymentReportPdfByDate({ year, month, type });
        title = t("relatorioPagamentos.documento.title", { month: MONTHS.find((m) => m.code === month)?.desc, year });
        filename = `relatorio-pagamentos-${year}-${month}.pdf`;
      }

      navigate("/documentos/pdf", {
        state: { url: resp.data.pdf, title, filename },
      });
    } catch (err) {
      toast.error(t("relatorioPagamentos.error.generate"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='row gy-3 align-items-end'>
              <div className='col-sm-3'>
                <label className='form-label'>{t("turma.field.academicYear")}</label>
                <select className='form-select' value={year} onChange={(e) => handleYearChange(Number(e.target.value))}>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-3'>
                <label className='form-label'>{t("relatorioPagamentos.field.month")}</label>
                <select className='form-select' value={month} onChange={(e) => handleMonthChange(e.target.value)}>
                  {MONTHS.map((m) => (
                    <option key={m.code} value={m.code}>
                      {m.desc}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-3'>
                <label className='form-label'>{t("relatorioDividas.field.type")}</label>
                <select className='form-select' value={type} onChange={(e) => setType(e.target.value)}>
                  <option value=''>{t("relatorioDividas.type.propinas")}</option>
                  <option value='extra'>{t("relatorioDividas.type.extras")}</option>
                  <option value='all'>{t("relatorioPagamentos.type.propinasExtra")}</option>
                </select>
              </div>
              <div className='col-sm-3'>
                <button type='button' className='btn btn-primary-600 w-100' onClick={handleGenerate} disabled={generating}>
                  {generating ? t("global.loading") : (<><i className='ri-file-chart-line' /> {t("relatorioPagamentos.action.generatePdf")}</>)}
                </button>
              </div>
            </div>

            <div className='row gy-3 align-items-end mt-1'>
              <div className='col-sm-3'>
                <label className='form-label'>{t("relatorioPagamentos.field.day")}</label>
                <select className='form-select' value={day} onChange={(e) => handleDayChange(e.target.value)}>
                  <option value=''>{t("relatorioPagamentos.day.none")}</option>
                  <option value='hoje'>{t("relatorioPagamentos.day.today")}</option>
                  <option value='ontem'>{t("relatorioPagamentos.day.yesterday")}</option>
                  <option value='semana'>{t("relatorioPagamentos.day.week")}</option>
                </select>
              </div>
              <div className='col-sm-3'>
                <label className='form-label'>{t("relatorioPagamentos.field.startDate")}</label>
                <input
                  type='date'
                  className='form-control'
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
              </div>
              <div className='col-sm-3'>
                <label className='form-label'>{t("relatorioPagamentos.field.endDate")}</label>
                <input
                  type='date'
                  className='form-control'
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                />
              </div>
              <div className='col-sm-3'>
                <span className='text-secondary-light text-sm'>{t("relatorioPagamentos.hint.dateFilter")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        {loading ? (
          <div className='text-center py-40'>{t("global.loading")}</div>
        ) : !stats ? (
          <div className='alert alert-info mb-0'>{t("relatorioPagamentos.hint.noData")}</div>
        ) : (
          <div className='row gy-3'>
            <div className='col-sm-3'>
              <div className='p-16 radius-8 bg-success-focus text-center'>
                <span className='text-secondary-light text-sm d-block'>{t("relatorioPagamentos.stat.studentsPaid")}</span>
                <strong className='text-success-main text-xl'>{stats.studentsPaid || 0}</strong>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='p-16 radius-8 bg-danger-focus text-center'>
                <span className='text-secondary-light text-sm d-block'>{t("relatorioPagamentos.stat.studentsUnpaid")}</span>
                <strong className='text-danger-main text-xl'>{stats.studentsUnPaid || 0}</strong>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='p-16 radius-8 bg-primary-50 text-center'>
                <span className='text-secondary-light text-sm d-block'>{t("relatorioPagamentos.stat.paidValue")}</span>
                <strong className='text-primary-600 text-xl'>{formatCurrencyMZN(stats.paidValue || 0)}</strong>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='p-16 radius-8 bg-warning-focus text-center'>
                <span className='text-secondary-light text-sm d-block'>{t("relatorioPagamentos.stat.unpaidValue")}</span>
                <strong className='text-warning-main text-xl'>{formatCurrencyMZN(stats.unPaidValue || 0)}</strong>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatorioPagamentosLayer;
